// services/v3/firebase.ts — app/db handles + server clock (SPEC §4.1)
// The legacy `services/firebaseInstance.ts` also initialises the default app,
// so every getter here must be guarded by getApps().
import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import { getDatabase, onValue, ref as dbRef, type Database } from 'firebase/database'
import { getFirestore, type Firestore } from 'firebase/firestore'
import { readV3Config } from './config'

export function getFirebaseApp(): FirebaseApp {
  if (getApps().length) return getApp()
  return initializeApp(readV3Config().firebase)
}

export function getRtdb(): Database {
  const cfg = readV3Config()
  const app = getFirebaseApp()

  // `getDatabase(app)` and `getDatabase(app, url)` are separate instances, and
  // initialising both against one repo throws "Database initialized multiple
  // times". Legacy `services/firebaseInstance.ts` uses the no-url form at module
  // scope, so match it whenever the app already carries the URL we want.
  const appUrl = app.options.databaseURL
  if (!cfg.firebase.databaseURL || appUrl === cfg.firebase.databaseURL) return getDatabase(app)
  return getDatabase(app, cfg.firebase.databaseURL)
}

export function getFs(): Firestore {
  return getFirestore(getFirebaseApp())
}

const SERVER_OFFSET_TIMEOUT_MS = 3000
let offsetPromise: Promise<number> | null = null

/**
 * Resolves the RTDB `.info/serverTimeOffset` once (first value wins).
 * Falls back to 0 after 3 s so the UI never blocks on a cold connection.
 */
export function getServerOffset(): Promise<number> {
  if (offsetPromise) return offsetPromise
  offsetPromise = new Promise<number>((resolve) => {
    let settled = false
    let unsub: (() => void) | null = null
    const done = (value: number) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      try { unsub?.() } catch { /* ignore */ }
      resolve(value)
    }
    const timer = setTimeout(() => {
      console.warn('[v3] serverTimeOffset timed out, falling back to 0')
      done(0)
    }, SERVER_OFFSET_TIMEOUT_MS) as unknown as number

    try {
      unsub = onValue(
        dbRef(getRtdb(), '.info/serverTimeOffset'),
        (snap) => {
          const v = snap.val()
          done(typeof v === 'number' && Number.isFinite(v) ? v : 0)
        },
        () => done(0),
      )
    } catch {
      done(0)
    }
  })
  return offsetPromise
}

/** test/teardown helper — forget the memoised offset */
export function resetServerOffset(): void {
  offsetPromise = null
}
